import React, { useState, useEffect } from 'react';
import { Ship, Globe, Briefcase, Package, Truck, Plane, Search, Shield, BarChart, CheckCircle } from 'lucide-react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { useLocation } from 'react-router-dom';

const Services = () => {
    const [isVisible, setIsVisible] = useState({});
    const location = useLocation();

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
                    }
                });
            },
            { threshold: 0.1 }
        );

        const elements = document.querySelectorAll('.animate-on-scroll');
        elements.forEach((el) => observer.observe(el));

        if (location.hash) {
            const element = document.getElementById(location.hash.replace('#', ''));
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }

        return () => observer.disconnect();
    }, [location]);

    const serviceSections = [
        {
            id: 'export',
            title: "Export Services",
            icon: <Globe className="w-12 h-12" />,
            description: "We handle the entire export lifecycle, ensuring seamless movement of goods across borders.",
            color: "from-green-500 to-green-700",
            lightColor: "bg-green-50",
            features: ["Market Research & Analysis", "Buyer Identification", "Export Documentation", "Compliance Management"]
        },
        {
            id: 'import',
            title: "Import Solutions",
            icon: <Package className="w-12 h-12" />,
            description: "Efficient sourcing and importing of high-quality raw materials and finished goods.",
            color: "from-emerald-500 to-emerald-700",
            lightColor: "bg-emerald-50",
            features: ["Supplier Sourcing", "Quality Control", "Customs Clearance", "Landed Cost Calculation"]
        },
        {
            id: 'logistics',
            title: "Logistics & Shipping",
            icon: <Ship className="w-12 h-12" />,
            description: "End-to-end logistics solutions via Sea, Air, and Land transport modes.",
            color: "from-teal-500 to-teal-700",
            lightColor: "bg-teal-50",
            features: ["Freight Forwarding", "Warehousing", "Real-time Tracking", "Multi-modal Transport"]
        },
        {
            id: 'consulting',
            title: "Trade Consulting",
            icon: <Briefcase className="w-12 h-12" />,
            description: "Strategic advice to help you navigate the complexities of international trade.",
            color: "from-lime-600 to-lime-800",
            lightColor: "bg-lime-50",
            features: ["Trade Policy Advice", "Risk Management", "Supply Chain Optimization", "Legal Advisory"]
        }
    ];

    const logisticsTypes = [
        { icon: <Ship className="w-8 h-8" />, label: "Sea Freight", desc: "Cost-effective for large volumes" },
        { icon: <Plane className="w-8 h-8" />, label: "Air Freight", desc: "Express delivery worldwide" },
        { icon: <Truck className="w-8 h-8" />, label: "Land Transport", desc: "Efficient domestic distribution" }
    ];

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 text-white py-24 overflow-hidden">
                    <div className="absolute inset-0 bg-black opacity-20"></div>
                    <div className="absolute inset-0">
                        <div className="absolute top-20 left-10 w-96 h-96 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
                        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-down">
                            Our <span className="text-green-300">Services</span>
                        </h1>
                        <p className="text-xl text-green-100 max-w-2xl mx-auto animate-fade-in-up">
                            Comprehensive international trade solutions tailored to meet your business goals.
                        </p>
                    </div>
                </section>

                {/* Main Services */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-24">
                    {serviceSections.map((service, index) => (
                        <section
                            key={service.id}
                            id={service.id}
                            className={`flex flex-col md:flex-row gap-12 items-center animate-on-scroll ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
                            style={{
                                opacity: isVisible[service.id] ? 1 : 0,
                                transform: isVisible[service.id] ? 'translateY(0)' : 'translateY(40px)',
                                transition: 'all 0.8s ease-out'
                            }}
                        >
                            <div className="w-full md:w-1/2">
                                <div className={`inline-block p-4 rounded-2xl bg-gradient-to-br ${service.color} text-white mb-6 shadow-lg`}>
                                    {service.icon}
                                </div>
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{service.title}</h2>
                                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                    {service.description}
                                </p>
                                <ul className="space-y-4">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx} className={`flex items-center gap-3 text-gray-700 ${service.lightColor} p-3 rounded-lg hover:bg-white hover:shadow-md transition-all border border-gray-100`}>
                                            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${service.color}`}></div>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="w-full md:w-1/2">
                                <div className="relative">
                                    <div className={`absolute inset-0 bg-gradient-to-br ${service.color} rounded-3xl transform rotate-3 opacity-20`}></div>
                                    <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                                        {/* Placeholder visual/graphic for each service */}
                                        <div className="aspect-video bg-gray-50 rounded-2xl flex items-center justify-center overflow-hidden">
                                            {service.id === 'logistics' ? (
                                                <div className="grid grid-cols-3 gap-4 w-full px-4">
                                                    {logisticsTypes.map((type, i) => (
                                                        <div key={i} className="text-center">
                                                            <div className="bg-white p-3 rounded-xl shadow-sm inline-block mb-2 text-green-600">
                                                                {type.icon}
                                                            </div>
                                                            <div className="text-xs font-semibold">{type.label}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className={`text-9xl text-gray-200 opacity-50`}>
                                                    {service.icon}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    ))}
                </div>

                {/* Process Section */}
                <section className="bg-gradient-to-br from-gray-50 to-green-50 py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How We Work</h2>
                            <p className="text-gray-600">A streamlined process for your peace of mind</p>
                        </div>
                        <div className="grid md:grid-cols-4 gap-8">
                            {[
                                { step: "01", title: "Consultation", desc: "Understanding your requirements" },
                                { step: "02", title: "Strategy", desc: "Developing a custom plan" },
                                { step: "03", title: "Execution", desc: "Implementing the solution" },
                                { step: "04", title: "Delivery", desc: "Ensuring timely completion" }
                            ].map((item, index) => (
                                <div key={index} className="relative text-center group">
                                    <div className="text-6xl font-bold text-gray-100 mb-4 group-hover:text-green-200 transition-colors">
                                        {item.step}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2 relative z-10">{item.title}</h3>
                                    <p className="text-gray-500 relative z-10">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <style jsx>{`
                    @keyframes fade-in-down {
                        from { opacity: 0; transform: translateY(-20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes fade-in-up {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes blob {
                        0%, 100% { transform: translate(0, 0) scale(1); }
                        33% { transform: translate(30px, -50px) scale(1.1); }
                        66% { transform: translate(-20px, 20px) scale(0.9); }
                    }
                    .animate-fade-in-down { animation: fade-in-down 1s ease-out; }
                    .animate-fade-in-up { animation: fade-in-up 1s ease-out 0.3s both; }
                    .animate-blob { animation: blob 7s infinite; }
                    .animation-delay-2000 { animation-delay: 2s; }
                `}</style>
            </div>
            <Footer />
        </>
    );
};

export default Services;
